<template>
  <!-- v-show, а не v-if: при глобальном «скрыть описания» текст остаётся в
       разметке (display:none), поэтому из индекса не пропадает -->
  <div v-show="!hidden" class="intro">
    <!-- Текст всегда в разметке целиком: обрезаем только стилями, иначе
         половина того, ради чего раздел и писался, пропала бы из индекса -->
    <p ref="textEl" class="intro__text" :class="{ 'intro__text--clamped': !expanded }">
      {{ text }}
    </p>

    <button
      v-if="clampable"
      type="button"
      class="intro__toggle"
      :aria-expanded="expanded"
      @click="expanded = !expanded"
    >
      {{ expanded ? "Свернуть" : "Читать дальше" }}
    </button>
  </div>
</template>

<script setup>
import { useResizeObserver } from "@vueuse/core";
import { useIntroCollapsed } from "~/composables/useIntroPrefs";

// Вводный текст раздела из админки. На телефоне он разворачивался на треть
// десятка экранов и отодвигал витрину, поэтому там по умолчанию свёрнут до
// трёх строк. На десктопе по умолчанию развёрнут, но его тоже можно свернуть —
// выбор запоминается в cookie для конкретного раздела.
const props = defineProps({
  text: { type: String, required: true },
  // Ключ раздела (alias) — под ним запоминается свёрнутость
  storageKey: { type: String, default: "" },
  // Глобальное «скрыть описания всех разделов»
  hidden: { type: Boolean, default: false },
});

const CLAMP_LINES = 3;
// Ориентир до замера, чтобы кнопка была уже в серверной разметке и не
// появлялась рывком. Считаем по самому узкому экрану: там в строку влезает
// около 31 символа, то есть в три строки — примерно 90. Ошибаться безопаснее
// в сторону «кнопка нужна»: замер её потом уберёт, а вот длинный текст
// без кнопки прочитать было бы нельзя
const LIKELY_LONG = 90;

const { isMobile } = useDevice();
const collapsedMap = useIntroCollapsed();

// Развёрнут ли текст. Если посетитель уже переключал этот раздел — берём его
// выбор из cookie; иначе умолчание по устройству (десктоп — развёрнут,
// телефон — свёрнут). В cookie храним именно «свёрнуто», как отклонение
const expanded = computed({
  get() {
    const map = collapsedMap.value || {};
    if (props.storageKey && props.storageKey in map) {
      return !map[props.storageKey];
    }
    return !isMobile;
  },
  set(value) {
    const map = { ...(collapsedMap.value || {}) };
    if (props.storageKey) map[props.storageKey] = !value;
    collapsedMap.value = map; // переприсваивание — иначе cookie не обновится
  },
});

const textEl = ref(null);
// Кнопка нужна, только если текст правда не влезает в три строки: короткому
// разделу она предлагала бы развернуть то, что и так видно
const clampable = ref(props.text.trim().length > LIKELY_LONG);

// Сравниваем полную высоту текста с высотой трёх строк, а не с фактической
// высотой блока: так замер не зависит от того, успели ли примениться стили,
// и не сбивается, когда текст уже развёрнут
const measure = () => {
  const element = textEl.value;
  if (!element) return;
  const full = element.scrollHeight;
  // Ноль означает, что браузер ещё не разложил страницу. Решать по такому
  // замеру нельзя — оставляем предположение по длине текста
  if (!full) return;
  const lineHeight = parseFloat(getComputedStyle(element).lineHeight) || 24;
  clampable.value = full > lineHeight * CLAMP_LINES + 2;
};

onMounted(measure);

// Поворот телефона и изменение окна меняют и число строк, и саму
// необходимость обрезки — пересчитываем
useResizeObserver(textEl, measure);
</script>

<style scoped>
.intro {
  /* Ширина как у сетки товаров в ProductsList — текст и карточки стоят
     в одну колонку, без «ступеньки» по правому краю */
  max-width: 2200px;
  margin: 0 0 14px;
}

.intro__text {
  margin: 0;
  color: var(--el-text-color-regular);
  line-height: 1.55;
  /* В базе у текста несколько абзацев: без pre-line переносы схлопнулись бы
     в пробелы и получилась бы сплошная простыня. Так же сделано описание
     товара в карточке */
  white-space: pre-line;
  /* Глобальное правило p { text-align: justify } со страницы «О компании»
     достаёт и сюда, а на узком экране выключка по ширине рвёт строки */
  text-align: left;
}

/* Обрезка тем же набором свойств, что в ProductsList и RelatedProducts.
   Свёрнутость теперь одинаково работает на всех экранах — управляет ей
   класс, а не медиазапрос */
.intro__text--clamped {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  line-clamp: 3; /* CLAMP_LINES в скрипте */
  -webkit-line-clamp: 3;
  overflow: hidden;
}

.intro__toggle {
  margin-top: 4px;
  padding: 0;
  border: none;
  background: none;
  color: var(--el-color-primary);
  font: inherit;
  font-size: 14px;
  cursor: pointer;
}

.intro__toggle:hover {
  text-decoration: underline;
}
</style>
